import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import './index.css';

const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Admin = lazy(() => import('./pages/Admin'));
const Contacts = lazy(() => import('./pages/Contacts'));
const ContactDetail = lazy(() => import('./pages/ContactDetail'));
const DistributionNetworks = lazy(() => import('./pages/DistributionNetworks'));
const SubContractors = lazy(() => import('./pages/SubContractors'));
const Competition = lazy(() => import('./pages/Competition'));
const CompetitorDeepDive = lazy(() => import('./pages/CompetitorDeepDive'));
const Projects = lazy(() => import('./pages/Projects'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const ProjectDashboard = lazy(() => import('./pages/ProjectDashboard'));
const ProjectBoard = lazy(() => import('./pages/ProjectBoard'));
const Reports = lazy(() => import('./pages/Reports'));
const Calendar = lazy(() => import('./pages/Calendar'));
const DevDashboard = lazy(() => import('./pages/DevDashboard'));
const Settings = lazy(() => import('./pages/Settings'));
const MyVehicle = lazy(() => import('./pages/MyVehicle'));
const Locations = lazy(() => import('./pages/Locations'));
const LocationDetail = lazy(() => import('./pages/LocationDetail'));
const Placeholder = lazy(() => import('./pages/Placeholder'));
const Queries = lazy(() => import('./pages/Queries'));
const Measurements = lazy(() => import('./pages/Measurements'));
const Proposals = lazy(() => import('./pages/Proposals'));
const WorkOrders = lazy(() => import('./pages/WorkOrders'));
const MaterialOrders = lazy(() => import('./pages/MaterialOrders'));
const Invoices = lazy(() => import('./pages/Invoices'));
const Permits = lazy(() => import('./pages/Permits'));
const SubPortal = lazy(() => import('./pages/SubPortal'));
const GutterPortal = lazy(() => import('./pages/GutterPortal'));
const SubcontractorApply = lazy(() => import('./pages/SubcontractorApply'));
const InvoiceView = lazy(() => import('./pages/InvoiceView'));
const ProposalView = lazy(() => import('./pages/ProposalView'));
const ProposalInternalView = lazy(() => import('./pages/ProposalInternalView'));
const EmailPreferences = lazy(() => import('./pages/EmailPreferences'));
const GoBackCorrections = lazy(() => import('./pages/GoBackCorrections'));

const INTERNAL_ROLES = ['admin', 'branch_manager', 'project_manager', 'project_estimator'];

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ color: '#999', textAlign: 'center', marginTop: '50px' }}>Loading...</div>;
  if (!user) return <Navigate to="/login" />;
  // warehouse_staff users are locked to their assigned branch's Warehouse tab — send them there
  // instead of the general CRM (mirrors how subcontractor/vendor use dedicated portals, not the app).
  if (user.role === 'warehouse_staff') return <Navigate to="/locations" />;
  return children;
}

// Locations/branch pages: internal roles keep full access; warehouse_staff can only reach their
// assigned branch's Warehouse/Orders tabs (enforced further by branches.js + LocationDetail.js).
function WarehouseRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ color: '#999', textAlign: 'center', marginTop: '50px' }}>Loading...</div>;
  if (!user) return <Navigate to="/login" />;
  if (!INTERNAL_ROLES.includes(user.role) && user.role !== 'warehouse_staff') return <Navigate to="/" />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ color: '#999', textAlign: 'center', marginTop: '50px' }}>Loading...</div>;
  if (!user) return <Navigate to="/login" />;
  if (user.role !== 'admin') return <Navigate to="/" />;
  return children;
}

function SubContractorsRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ color: '#999', textAlign: 'center', marginTop: '50px' }}>Loading...</div>;
  if (!user) return <Navigate to="/login" />;
  if (!['admin', 'project_manager'].includes(user.role)) return <Navigate to="/" />;
  return children;
}

function InternalRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ color: '#999', textAlign: 'center', marginTop: '50px' }}>Loading...</div>;
  if (!user) return <Navigate to="/login" />;
  if (!INTERNAL_ROLES.includes(user.role)) return <Navigate to="/" />;
  return children;
}

function DevRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ color: '#999', textAlign: 'center', marginTop: '50px' }}>Loading...</div>;
  if (!user) return <Navigate to="/login" />;
  if (!user.isDeveloper && !INTERNAL_ROLES.includes(user.role)) return <Navigate to="/" />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ color: '#999', textAlign: 'center', marginTop: '50px' }}>Loading...</div>;
  return user ? <Navigate to="/" /> : children;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function RouteFallback() {
  return <div style={{ color: '#999', textAlign: 'center', marginTop: '50px' }}>Loading...</div>;
}

// Global UX behavior for all numeric inputs (quantity, price, margin, cost, etc. across
// proposals, work orders, pricing matrix, invoices, and every other numeric field): clicking
// or tabbing into the field selects its existing value so typing immediately overwrites it,
// instead of requiring the user to manually clear the field first.
function useSelectNumberInputOnFocus() {
  useEffect(() => {
    const handleFocusIn = (e) => {
      const el = e.target;
      if (el?.tagName === 'INPUT' && el.type === 'number') {
        el.select();
      }
    };
    document.addEventListener('focusin', handleFocusIn);
    return () => document.removeEventListener('focusin', handleFocusIn);
  }, []);
}

function App() {
  useSelectNumberInputOnFocus();
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* Publicly accessible routes. Keep metadata aligned in server/config/publicPageMetadata.js. */}
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/invoice/:id" element={<InvoiceView />} />
            <Route path="/proposal/:id" element={<ProposalView />} />
            <Route path="/email-preferences/:contactId" element={<EmailPreferences />} />
            <Route path="/portal/:token" element={<SubPortal />} />
            <Route path="/gutter-portal/:token" element={<GutterPortal />} />
            <Route path="/subcontractors/apply" element={<SubcontractorApply />} />

            {/* Authenticated CRM routes. */}
            <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            <Route path="/contacts" element={<PrivateRoute><Contacts /></PrivateRoute>} />
            <Route path="/contacts/:id" element={<PrivateRoute><ContactDetail /></PrivateRoute>} />
            <Route path="/subcontractors" element={<SubContractorsRoute><SubContractors /></SubContractorsRoute>} />
            <Route path="/competition" element={<AdminRoute><Competition /></AdminRoute>} />
            <Route path="/competition/:id" element={<AdminRoute><CompetitorDeepDive /></AdminRoute>} />
            <Route path="/vendors" element={<PrivateRoute><DistributionNetworks /></PrivateRoute>} />
            <Route path="/projects" element={<PrivateRoute><Projects /></PrivateRoute>} />
            <Route path="/board" element={<PrivateRoute><ProjectBoard /></PrivateRoute>} />
            <Route path="/reports" element={<PrivateRoute><Reports /></PrivateRoute>} />
            <Route path="/measurements" element={<PrivateRoute><Measurements /></PrivateRoute>} />
            <Route path="/projects/:id" element={<PrivateRoute><ProjectDetail /></PrivateRoute>} />
            <Route path="/projects/:id/dashboard" element={<PrivateRoute><ProjectDashboard /></PrivateRoute>} />
            <Route path="/proposals" element={<PrivateRoute><Proposals /></PrivateRoute>} />
            <Route path="/work-orders" element={<PrivateRoute><WorkOrders /></PrivateRoute>} />
            <Route path="/go-backs-corrections" element={<InternalRoute><GoBackCorrections /></InternalRoute>} />
            <Route path="/material-orders" element={<PrivateRoute><MaterialOrders /></PrivateRoute>} />
            <Route path="/invoices" element={<PrivateRoute><Invoices /></PrivateRoute>} />
            <Route path="/permits" element={<PrivateRoute><Permits /></PrivateRoute>} />
            <Route path="/documents" element={<PrivateRoute><Placeholder title="Documents" icon="📁" description="Store contracts, photos, and files." /></PrivateRoute>} />
            <Route path="/queries" element={<AdminRoute><Queries /></AdminRoute>} />
            <Route path="/calendar" element={<PrivateRoute><Calendar /></PrivateRoute>} />
            <Route path="/my-vehicle" element={<PrivateRoute><MyVehicle /></PrivateRoute>} />
            <Route path="/locations" element={<WarehouseRoute><Locations /></WarehouseRoute>} />
            <Route path="/locations/:id" element={<WarehouseRoute><LocationDetail /></WarehouseRoute>} />
            <Route path="/settings" element={<AdminRoute><Settings /></AdminRoute>} />
            <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
            <Route path="/dev" element={<DevRoute><DevDashboard /></DevRoute>} />
            <Route path="/proposal/:id/deep-look" element={<PrivateRoute><ProposalInternalView /></PrivateRoute>} />
            <Route path="/proposal/:id/pick-list" element={<PrivateRoute><ProposalInternalView /></PrivateRoute>} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
